/**
 * Workbench Configuration Constants
 * Colors, dimensions, keyboard shortcuts, and defaults for the Behavioral Workbench
 */

// Valence colors (from FGOS theme)
export const VALENCE_COLORS = {
  positive: '#23A38F', // Teal
  negative: '#FF3749', // Red/secondary
  neutral: '#0097B2',  // Blue
};

// Pastel valence colors for code chips on chunks (mockup design)
export const VALENCE_CHIP_COLORS = {
  positive: { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
  negative: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' },
  neutral:  { bg: '#e0e7ff', text: '#3730a3', border: '#6366f1' },
};

// AI Ghost Pill styling
export const AI_GHOST_COLORS = {
  bg: 'rgba(35, 163, 143, 0.08)',
  border: 'rgba(35, 163, 143, 0.4)',
  text: '#23A38F',
  hoverBg: '#23A38F',
  hoverText: '#fff',
};

// Leadership Quality colors (consistent across the platform)
export const DIMENSION_COLORS = {
  'Curiosity': '#F59E0B',
  'Courage': '#FF3749',
  'Vulnerability': '#C9A0DC',
  'Pursuit of Purpose and People': '#23A38F',
  'Learning Agility': '#D8A200',
  'Determination': '#6366F1',
  'Self and Situational Awareness': '#0097B2',
  'Cross-Cultural Communication': '#059669',
  'Adaptability': '#4A5568',
  'Conscientiousness': '#E11D48',
};

// Faana 10 Leadership Qualities in display order
export const FAANA_DIMENSIONS = [
  'Curiosity',
  'Courage',
  'Vulnerability',
  'Pursuit of Purpose and People',
  'Learning Agility',
  'Determination',
  'Self and Situational Awareness',
  'Cross-Cultural Communication',
  'Adaptability',
  'Conscientiousness',
];

// Short labels for RadarChart vertices (max 3-4 chars)
export const QUALITY_SHORT_LABELS = {
  'Curiosity': 'CUR',
  'Courage': 'CRG',
  'Vulnerability': 'VUL',
  'Pursuit of Purpose and People': 'PPP',
  'Learning Agility': 'LRN',
  'Determination': 'DET',
  'Self and Situational Awareness': 'SSA',
  'Cross-Cultural Communication': 'XCC',
  'Adaptability': 'ADP',
  'Conscientiousness': 'CON',
};

// Key aspects per Leadership Quality (from Faana methodology)
export const QUALITY_KEY_ASPECTS = {
  'Curiosity': 'Drive to explore, question, and seek understanding',
  'Courage': 'Facing challenges and taking calculated risks despite fear',
  'Vulnerability': 'Openness to admitting mistakes and seeking help',
  'Pursuit of Purpose and People': 'Mission alignment and relationship investment',
  'Learning Agility': 'Speed of acquiring and applying new knowledge',
  'Determination': 'Persistence in pursuing goals despite obstacles',
  'Self and Situational Awareness': 'Understanding personal patterns and context',
  'Cross-Cultural Communication': 'Effectiveness across cultural boundaries',
  'Adaptability': 'Flexibility in changing circumstances',
  'Conscientiousness': 'Taking ownership of actions and outcomes with integrity',
};

// Keyboard shortcuts configuration
export const KEYBOARD_SHORTCUTS = {
  // Code application (1-9 applies codes with those shortcuts)
  applyCode: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],

  // Navigation
  nextChunk: ['n', 'Enter'],
  prevChunk: ['p'],
  nextDocument: [']'],
  prevDocument: ['['],

  // Actions
  openNewCode: ['Shift+n'],
  focusSearch: ['Control+f', 'Meta+f'],
  showShortcuts: ['?'],
  escape: ['Escape'],

  // AI suggestions (Stage 3)
  acceptSuggestion: ['a'],
  rejectSuggestion: ['x'],
};

// Panel layout defaults
export const PANEL_WIDTHS = {
  left: 280,      // Code browser
  right: 360,     // Coding panel
  minLeft: 200,
  maxLeft: 400,
  minRight: 300,
  maxRight: 500,
};

// Document status badges
export const DOCUMENT_STATUS = {
  current: {
    label: 'Current',
    color: '#23A38F',
    bgColor: 'rgba(35, 163, 143, 0.1)',
  },
  inProgress: {
    label: 'In Progress',
    color: '#D8A200',
    bgColor: 'rgba(216, 162, 0, 0.1)',
  },
  new: {
    label: 'New',
    color: '#0097B2',
    bgColor: 'rgba(0, 151, 178, 0.1)',
  },
  complete: {
    label: 'Complete',
    color: '#767676',
    bgColor: 'rgba(118, 118, 118, 0.1)',
  },
};

// Chunk selection states (left-border accent style)
export const CHUNK_STATES = {
  default: {
    borderLeft: 'transparent',
    bg: 'transparent',
  },
  selected: {
    borderLeft: '#141B2D',
    bg: 'rgba(20, 27, 45, 0.04)',
  },
  coded: {
    borderLeft: '#23A38F',
    bg: 'rgba(35, 163, 143, 0.04)',
  },
  aiSuggested: {
    borderLeft: '#0097B2',
    bg: 'rgba(0, 151, 178, 0.04)',
  },
};

// Default confidence value for manual coding
export const DEFAULT_CONFIDENCE = 0.9;

// Notes field max length
export const MAX_NOTES_LENGTH = 2000;

// Pagination
export const CHUNKS_PER_PAGE = 50;

// Tab indices
export const TABS = {
  CODING: 0,
  INTELLIGENCE: 1,
  ENTITIES: 2,
};
